import React, { useState, useRef, useEffect } from 'react';
import { translate } from '../../../lib/lang.helper';
import { selectLang } from '../../../store/ui/ui.selector';
import { useDispatch, useSelector } from 'react-redux';
import { sendSingleRequest } from '../../../apis';
import { Feedback } from '../../shared';
import { loadFarmsData } from '../../../store/farms/farms.actions';
import { IGrowingSeed } from '../../../entities/growing.entities';

function autoAssignBaskets(
  points: IGrowingSeed[],
  totalCount: number,
): IGrowingSeed[] {
  const result = [...points];
  let lastUsed = 0;

  for (const seed of result) {
    if (seed.start_position == null || seed.end_position == null) {
      const start = lastUsed + 1;
      const end = start + (seed.line_length ?? seed.basket_count ?? 0) - 1;
      seed.start_position = start;
      seed.end_position = end;
      lastUsed = end;
    } else {
      lastUsed = Math.max(lastUsed, seed.end_position);
    }
    if (seed.start_position < 1) seed.start_position = 1;
    if (seed.end_position > totalCount) seed.end_position = totalCount;
  }
  return result;
}

function getOccupiedArray(
  seeds: IGrowingSeed[],
  movableSeed: string | number,
  totalCount: number,
): boolean[] {
  const occupied = new Array<boolean>(totalCount + 1).fill(false);

  seeds.forEach(s => {
    if (s.id === movableSeed) return;
    const start = s.start_position ?? 1;
    const end = s.end_position ?? 1;
    for (let b = start; b <= end; b++) {
      occupied[b] = true;
    }
  });

  return occupied;
}

function collides(
  startBasket: number,
  length: number,
  occupied: boolean[],
): boolean {
  for (let b = startBasket; b < startBasket + length; b++) {
    if (occupied[b]) return true;
  }
  return false;
}

function snapToEmpty(
  candidate: number,
  seedLength: number,
  occupied: boolean[],
): number {
  const totalLine = occupied.length - 1;

  if (candidate < 1) candidate = 1;
  if (candidate + seedLength - 1 > totalLine) {
    candidate = totalLine - seedLength + 1;
  }

  if (!collides(candidate, seedLength, occupied)) {
    return candidate;
  }

  let offset = 1;
  while (true) {
    const leftTry = candidate - offset;
    const rightTry = candidate + offset;
    let outOfBoundsLeft = false;
    let outOfBoundsRight = false;

    if (leftTry >= 1) {
      if (!collides(leftTry, seedLength, occupied)) {
        return leftTry;
      }
    } else {
      outOfBoundsLeft = true;
    }

    if (rightTry + seedLength - 1 <= totalLine) {
      if (!collides(rightTry, seedLength, occupied)) {
        return rightTry;
      }
    } else {
      outOfBoundsRight = true;
    }

    if (outOfBoundsLeft && outOfBoundsRight) {
      return candidate;
    }

    offset++;
    if (offset > totalLine) {
      return candidate;
    }
  }
}

interface Props {
  lineID: number;
  points: IGrowingSeed[];
  movableSeed: number;
  capacity: number;
  onClose: () => void;
}

const SeedingsOrder = ({
  lineID,
  points,
  movableSeed,
  capacity: lineCapacity,
  onClose,
}: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);

  const [seeds, setSeeds] = useState<IGrowingSeed[]>(() =>
    autoAssignBaskets(points, lineCapacity),
  );
  const [errorMsg, setErrorMsg] = useState<string | undefined>();
  const [isDragging, setIsDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [originalLeftBasket, setOriginalLeftBasket] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const occupied = getOccupiedArray(seeds, movableSeed, lineCapacity);

  function updateMovableSeedPosition(newStart: number) {
    setSeeds(prev =>
      prev.map(s => {
        if (s.id !== movableSeed) return s;
        const length = s.line_length ?? s.basket_count ?? 0;
        return {
          ...s,
          start_position: newStart,
          end_position: newStart + length - 1,
        };
      }),
    );
  }

  function handleMouseDown(
    e: React.MouseEvent<HTMLDivElement>,
    seed: IGrowingSeed,
  ) {
    e.preventDefault();
    e.stopPropagation();
    if (seed.id !== movableSeed) return;

    setIsDragging(true);
    setDragStartX(e.clientX);

    const startBasket = seed.start_position ?? 1;
    setOriginalLeftBasket(startBasket);
  }
  function handleMouseMove(e: MouseEvent) {
    if (!isDragging) return;
    e.preventDefault();

    const container = containerRef.current;
    if (!container) return;

    const seed = seeds.find(s => s.id === movableSeed);
    if (!seed) return;

    const seedLength =
      (seed.end_position ?? 0) - (seed.start_position ?? 0) + 1;

    const deltaPx = e.clientX - dragStartX;

    const containerWidth = container.offsetWidth;
    if (containerWidth <= 0) return;

    const basketsDelta = (deltaPx / containerWidth) * lineCapacity;
    const candidate = Math.round(originalLeftBasket + basketsDelta);

    const newStart = snapToEmpty(candidate, seedLength, occupied);
    updateMovableSeedPosition(newStart);
  }

  function handleMouseUp() {
    if (isDragging) {
      setIsDragging(false);
    }
  }

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStartX, originalLeftBasket, seeds]);

  function getSeedStyle(s: IGrowingSeed): React.CSSProperties {
    const start = s.start_position ?? 1;
    const end = s.end_position ?? 1;
    const length = end - start + 1;

    const leftPct = ((start - 1) / lineCapacity) * 100;
    const widthPct = (length / lineCapacity) * 100;

    const isMovable = s.id === movableSeed;

    return {
      position: 'absolute',
      top: 0,
      left: `${leftPct}%`,
      width: `${widthPct}%`,
      height: '100%',
      backgroundColor: isMovable ? 'green' : 'gray',
      cursor: isMovable ? 'grab' : 'auto',
      userSelect: 'none',
      minWidth: 'fit-content',
      zIndex: isMovable ? 1 : undefined,
    };
  }

  const handleSave = async () => {
    setSaving(true);
    const response = await sendSingleRequest(
      {
        line_id: lineID,
        seedings: seeds.map(({ id, start_position, end_position }) => ({
          seed_id: id,
          start_position,
          end_position,
        })),
      },
      'POST',
      `api/farm/line/seeding-position`,
      true,
    );

    if (response.status) {
      await dispatch(loadFarmsData());
      setSaving(false);
      onClose();
    } else {
      setSaving(false);
      setErrorMsg(translate(lang, response.data.message));
    }
  };

  return (
    <div style={{ margin: '1rem 0' }}>
      <h5>{translate(lang, 'Move Seeding')}</h5>
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          width: '100%',
          height: '30px',
          background: '#eee',
          border: '1px solid #ccc',
          overflow: 'hidden',
          marginTop: '1rem',
        }}
      >
        {seeds.map(seed => (
          <div
            key={String(seed.id)}
            style={getSeedStyle(seed)}
            onMouseDown={e => handleMouseDown(e, seed)}
          >
            <span style={{ color: '#fff', fontSize: 12, marginLeft: 5 }}>
              {seed.id}
            </span>
          </div>
        ))}
      </div>
      <div className='mt-10'>
        <button
          className='rsp-btn button button--2 button--blue button--small button--fill'
          onClick={e => onClose()}
        >
          {translate(lang, 'Close')}
        </button>
        <button
          className='rsp-btn button ml-10 button--2 button--green button--small button--fill'
          onClick={e => handleSave()}
        >
          {saving ? translate(lang, 'Saving...') : translate(lang, 'Save')}
        </button>
      </div>
      {!!errorMsg && (
        <Feedback
          message={translate(lang, errorMsg)}
          type={'error'}
          theme='light'
          position={10}
          isGlobal
          onClose={() => setErrorMsg(undefined)}
        />
      )}
    </div>
  );
};

export default SeedingsOrder;
